import React, { useState, useEffect } from 'react';
import { collection, orderBy, query, limit, doc, getDoc, onSnapshot, where, getDocs, updateDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { MediaItem, getThumbnail } from '../types';
import Modal from '../components/Modal';
import EditorModal from '../components/EditorModal';
import slugify from 'slugify';

type TabType = 'all' | 'inProgress' | 'completed';

const Products: React.FC = () => {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<MediaItem[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('inProgress');
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<MediaItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
  const [currentShopId, setCurrentShopId] = useState<string>('');
  const [shopInput, setShopInput] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const { currentUser } = useAuth();

  // Initialize with user's default shop and check admin status
  useEffect(() => {
    const initializeShop = async () => {
      if (!currentUser) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const shopId = userData.shop?.$key || userData.shopId;
          if (shopId) {
            setCurrentShopId(shopId);
          }
          // Check if user is admin
          setIsAdmin(userData.isAdmin || false);
        }
      } catch (error) {
        console.error('Error getting user shop:', error);
      }
    };

    initializeShop();
  }, [currentUser]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupRealtimeListener = async () => {
      try {
        if (!currentUser || !currentShopId) {
          setLoading(false);
          return;
        }

        const q = query(
          collection(db, 'shops', currentShopId, 'products'),
          orderBy('createdAt', 'desc'),
          limit(200)
        );

        // Set up realtime listener
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          const fetchedItems: MediaItem[] = [];
          querySnapshot.forEach((doc) => {
            fetchedItems.push({
              id: doc.id,
              ...doc.data()
            } as MediaItem);
          });
          setItems(fetchedItems);
          setFilteredItems(fetchedItems);
          setLoading(false);
        }, (error) => {
          console.error('Error in realtime listener:', error);
          setLoading(false);
        });

      } catch (error) {
        console.error('Error setting up realtime listener:', error);
        setLoading(false);
      }
    };

    setupRealtimeListener();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser, currentShopId]);

  useEffect(() => {
    const filterItems = () => {
      switch (activeTab) {
        case 'inProgress':
          return items.filter(item => item.description?.includes('Type of outfit'));
        case 'completed':
          return items.filter(item => !item.description?.includes('Type of outfit'));
        case 'all':
        default:
          return items;
      }
    };

    setFilteredItems(filterItems());
  }, [activeTab, items]);

  const handleItemClick = (item: MediaItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleItemEdit = (item: MediaItem) => {
    setSelectedItem(item);
    setIsEditorModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const handleCloseEditorModal = () => {
    setIsEditorModalOpen(false);
    setSelectedItem(null);
  };


  const handleShopChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shopInput.trim()) return;

    setLoading(true);

    try {
      // Only process as username
      await changeShopByUsername(shopInput.trim());
    } catch (error) {
      console.error('Error changing shop:', error);
      setLoading(false);
    }
  };

  const changeShopByUsername = async (username: string) => {
    if (!isAdmin) {
      alert('There was an error');
      setLoading(false);
      return;
    }

    try {
      // Clean and slugify the username
      const cleanUsername = slugify(username)
        .toLowerCase()
        .replace(/[^a-zA-Z ]/g, '');

      // Query shops collection for matching username
      const shopsQuery = query(
        collection(db, 'shops'),
        where('username', '==', cleanUsername)
      );

      const shopSnapshot = await getDocs(shopsQuery);

      if (!shopSnapshot.empty) {
        const shopDoc = shopSnapshot.docs[0];
        const shopId = shopDoc.id;

        // Update user's shop reference
        if (currentUser) {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            'shop.$key': shopId
          });
        }

        // Set the current shop
        setCurrentShopId(shopId);
        setShopInput(shopId);
      } else {
        console.log('No shops found with the provided username');
        alert('No shop found with that username');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error looking up shop by username:', error);
      alert('Error looking up shop');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>

      {/* Shop Selection */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-2">
        <form onSubmit={handleShopChange} className="flex items-center space-x-3">
          <label htmlFor="username" className="text-sm font-medium text-gray-700">
            Username:
          </label>
          <input
            id="username"
            type="text"
            value={shopInput}
            onChange={(e) => setShopInput(e.target.value)}
            placeholder="Enter username"
            className="flex-1 max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : 'Change Shop'}
          </button>
        </form>
        {currentShopId && (
          <p className="mt-2 text-sm text-gray-600">
            <span className="font-medium"></span>
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('inProgress')}
            className={`${activeTab === 'inProgress' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            In Progress
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`${activeTab === 'completed' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Completed
          </button>
        </nav>
      </div>

      {/* Pinterest-style Masonry Grid */}
      <main className="max-w-screen-2xl mx-auto px-2 sm:px-4 lg:px-6 py-4">
        {filteredItems.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Products yet</h3>
            <p className="text-gray-500">
              {currentShopId ? 'This shop has no products yet.' : 'Enter a shop ID to view products.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2 sm:gap-3 md:gap-4">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="cursor-pointer group"
                onClick={() => handleItemEdit(item)}
              >
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">

                  <div className="relative overflow-hidden rounded-2xl">
                    <img
                      src={getThumbnail(item, 'grid')}
                      alt={item.name || 'Video Pin'}
                      className="w-full h-auto object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <div className="flex gap-2">

                        <div className="bg-white bg-opacity-90 rounded-full p-2 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                          <svg className="w-5 h-5 text-gray-800" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>


                  {(item.name || item.description) && (
                    <div className="p-3">
                      {item.name && (
                        <h3 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2 leading-tight">
                          {item.name}
                        </h3>
                      )}
                      {item.description && (
                        <p className="text-gray-600 text-xs line-clamp-2 leading-relaxed">
                          {item.description}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Modal */}
      <Modal
        item={selectedItem}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      {/* Editor Modal */}
      <EditorModal
        item={selectedItem}
        isOpen={isEditorModalOpen}
        onClose={handleCloseEditorModal}
        shopId={currentShopId}
      />
    </div>
  );
};

export default Products;
